import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { stopTts } from '../../lib/tts';
import type { Lang } from './translations';

type LangCtx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
};

const Ctx = createContext<LangCtx | null>(null);
const STORAGE_KEY = 'admin.lang';

export function AdminLangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'hi' || saved === 'en') return saved;
    } catch { /* SSR or blocked storage — fall through */ }
    return 'en';
  });

  const setLang = useCallback((l: Lang) => {
    // Cancel any ongoing speech when language changes so a half-spoken
    // English sentence doesn't collide with a new Hindi one.
    stopTts();
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => setLang(lang === 'en' ? 'hi' : 'en'), [lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang === 'hi' ? 'hi' : 'en';
  }, [lang]);

  return <Ctx.Provider value={{ lang, setLang, toggle }}>{children}</Ctx.Provider>;
}

export function useAdminLang(): LangCtx {
  const v = useContext(Ctx);
  if (!v) {
    // Permissive fallback so the rest of the admin UI still works if
    // a component is rendered outside the provider (e.g. login page).
    return { lang: 'en', setLang: () => {}, toggle: () => {} };
  }
  return v;
}
